/**
 * SocialExposure Component
 *
 * This component manages the chat interface display throughout the experiment.
 * It switches between AI chat and player-to-player chat based on the current round
 * and player markers.
 *
 * Round Logic:
 * - Round 2 (Information Phase):
 *   - marker=0, secondary_marker="ai" → AI chat (ChatWithAI)
 *   - All other players → null (they see PDF or wait)
 * - Round 4 (Communication Phase):
 *   - All players → Player-to-player chat (CustomChat)
 *
 * This conditional rendering is key to the experimental design:
 * - Teacher (marker=0) learns via AI or PDF
 * - Student (marker>0) waits during learning phase
 * - Then Teacher teaches Student through chat
 *
 * Component Hierarchy:
 * SocialExposure → ChatWithAI (Round 2, Teacher with AI only)
 *               → CustomChat (Round 4, all players)
 */

import React from "react";
import { usePlayer, useRound, useGame } from "@empirica/core/player/classic/react";
import { ChatWithAI } from "./ChatWithAI";
import { CustomChat } from "./CustomChat";

/**
 * Parse instruction string and render as numbered list
 * Handles nested parentheses in points like "(a+b = -B/A)"
 */
function LearningOutcomes({ instruction, color = "blue" }) {
  if (!instruction) return null;

  const colorClasses = {
    blue: "text-blue-800",
    emerald: "text-emerald-800"
  };

  let points = [];

  // Try to parse "(1) ... (2) ... (3) ..." format
  const parenPattern = /\((\d+)\)\s*/g;
  const parenMatches = [...instruction.matchAll(parenPattern)];

  if (parenMatches.length > 0) {
    for (let i = 0; i < parenMatches.length; i++) {
      const startIdx = parenMatches[i].index + parenMatches[i][0].length;
      const endIdx = i + 1 < parenMatches.length ? parenMatches[i + 1].index : instruction.length;
      const content = instruction.slice(startIdx, endIdx).trim();
      if (content) {
        points.push(content);
      }
    }
  }

  // Fallback: try "1. ... 2. ... 3. ..." format
  if (points.length === 0) {
    const dotPattern = /(\d+)\.\s*/g;
    const dotMatches = [...instruction.matchAll(dotPattern)];

    if (dotMatches.length > 0) {
      for (let i = 0; i < dotMatches.length; i++) {
        const startIdx = dotMatches[i].index + dotMatches[i][0].length;
        const endIdx = i + 1 < dotMatches.length ? dotMatches[i + 1].index : instruction.length;
        const content = instruction.slice(startIdx, endIdx).trim();
        if (content) {
          points.push(content);
        }
      }
    }
  }

  if (points.length > 0) {
    return (
      <ul className={`text-xs space-y-2 ${colorClasses[color]}`}>
        {points.map((point, idx) => (
          <li key={idx} className="flex items-start gap-2">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-white flex items-center justify-center text-xs font-bold shadow-sm">
              {idx + 1}
            </span>
            <span className="pt-0.5 leading-relaxed">{point}</span>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <p className={`text-xs leading-relaxed ${colorClasses[color]}`}>
      {instruction}
    </p>
  );
}

export function SocialExposure() {
  const player = usePlayer();
  const round = useRound();
  const game = useGame();
  const marker = player.get("marker");
  const secondaryMarker = player.get("secondary_marker");

  // Get topic name from config
  const config = game?.get("config");
  const topicName = config?.name || "the topic";

  // Debug: log config to see what we're getting
  console.log("SocialExposure config:", {
    config,
    gameData: config?.gameData,
    instructionExplainer: config?.instructionExplainer,
    instructionLearner: config?.instructionLearner,
  });

  // Get round name for dual-mode compatibility
  const roundName = round.get("name");
  const gameMode = game?.get("gameMode") || "dyad";
  const isSingleMode = gameMode === "single";

  // Information Phase: Show ChatWithAI for Teacher/Learner (marker=0) with AI conditions
  // Works for both DYAD (Teacher) and SINGLE (Learner) modes
  if (roundName === "Information Phase") {
    if (marker === 0 && (secondaryMarker === "ai" || secondaryMarker === "ai-fixed")) {
      return (
        <div className="social-exposure h-full w-full flex flex-col">
          <ChatWithAI />
        </div>
      );
    }
    // For other players in Information Phase, return null (they see PDF or wait)
    return null;
  }

  // Determine role
  const isTeacher = marker === 0;

  // Round 5: Player-to-player chat with two-column layout
  return (
    <div className="h-full w-full flex">
      {/* Left Column: Learning Objectives */}
      <div className="w-64 flex-shrink-0 bg-gray-50 border-r border-gray-200 p-3 overflow-y-auto">
        {/* Role & Topic Header */}
        <div className={`rounded-lg p-3 mb-3 ${isTeacher ? 'bg-blue-600' : 'bg-emerald-600'} text-white`}>
          <span className="text-xs font-medium uppercase tracking-wide opacity-80">
            {isTeacher ? 'Teacher' : 'Student'}
          </span>
          <h2 className="text-sm font-bold leading-tight mt-1">{topicName}</h2>
        </div>

        {/* (1) Information-asymmetry framing — moved to the top so it's read first
            and frames the points below. States the situation + goal only (no
            delivery styling); identical across conditions. */}
        <div className={`rounded-lg p-3 mb-3 border ${isTeacher ? 'bg-blue-50 border-blue-200' : 'bg-emerald-50 border-emerald-200'}`}>
          <p className={`text-xs font-semibold leading-relaxed ${isTeacher ? 'text-blue-800' : 'text-emerald-800'}`}>
            {isTeacher
              ? "Your partner hasn't seen this material, and you'll both be quizzed. Your job is to teach them."
              : "You haven't seen this material, and you'll be quizzed too. Ask for whatever you need to learn it."}
          </p>
        </div>

        {/* (2) Learning objectives — the visual anchor of the panel */}
        {isTeacher ? (
          <div className="rounded-lg p-3 mb-3 border-2 border-blue-300 bg-white shadow-sm select-none">
            <h3 className="text-sm font-bold text-blue-800 mb-1">
              Teach These Points
            </h3>
            <p className="text-xs text-blue-700 mb-3 font-medium">
              Make sure your partner understands:
            </p>
            <LearningOutcomes
              instruction={config?.instructionExplainer}
              color="blue"
            />
          </div>
        ) : (
          <div className="rounded-lg p-3 mb-3 border-2 border-emerald-300 bg-white shadow-sm select-none">
            <h3 className="text-sm font-bold text-emerald-800 mb-1">
              Learn These Points
            </h3>
            <p className="text-xs text-emerald-700 mb-3 font-medium">
              Ask your partner to explain:
            </p>
            <LearningOutcomes
              instruction={config?.instructionLearner}
              color="emerald"
            />
          </div>
        )}

        {/* (3) Tips — quieted so they don't compete with the objectives above */}
        <div className="rounded-lg p-3 mb-3">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
            {isTeacher ? "Teaching Tips" : "Learning Tips"}
          </h3>
          {isTeacher ? (
            <ul className="text-xs text-gray-500 space-y-1">
              <li>• Explain clearly using simple language</li>
              <li>• Answer questions and explain why</li>
              <li>• Give examples to help understanding</li>
            </ul>
          ) : (
            <ul className="text-xs text-gray-500 space-y-1">
              <li>• Ask clarifying questions</li>
              <li>• Request examples</li>
              <li>• Summarize what you learn</li>
            </ul>
          )}
        </div>

        {/* (4) Quiz reminder — short closing nudge */}
        <div className="p-2 bg-gray-100 border border-gray-200 rounded-lg">
          <p className="text-xs text-gray-600">
            <strong>Remember:</strong> you'll be tested on this topic{isTeacher ? ' too' : ''}.
          </p>
        </div>
      </div>

      {/* Right Column: Chat */}
      <div className="flex-1 min-w-0 h-full flex flex-col overflow-hidden">
        <CustomChat scope={round} attribute="chat" formulas={config?.formulas} />
      </div>
    </div>
  );
}